import { ReminderQuery } from '../queries/reminder_query';
import { IPersonalReminder, IReminderData } from '../personal_reminder_interface';
import { IWeekDay, IDay } from 'ui-ng-mobile-date-picker';
import { TypeUtility } from 'utility-ts-type';
import * as _4 from 'lodash4';

interface ISeperatedTimeObject {
  due_dt: string;
  due_tm: string;
}

export class PersonalReminderService {
  personalReminderData: IPersonalReminder;
  isTitleValid: boolean;
  tempSelectedDay: IWeekDay;
  tempSelectedTime: IDay;

  constructor(
    private accountModel: any,
    private ngTypeUtility: typeof TypeUtility,
    private ngReminderQuery: ReminderQuery
  ) {
    this.personalReminderData = {} as IPersonalReminder;
    this.personalReminderData.isAllDayReminder = false;
    this.isTitleValid = false;

    // Initialize dates for modal and the modal itself
    this.tempSelectedDay = this.personalReminderData.selectedDay;
    this.tempSelectedTime = this.personalReminderData.selectedTime;
  }

  setTitleAndValidate(titleText: string, isValid: boolean) {
    this.personalReminderData.reminderTitle = titleText;
    this.isTitleValid = isValid;
  }

  toggleAllDaySwitch(currentState: boolean): void {
    this.personalReminderData.isAllDayReminder = currentState;
    this.updateReminderDueDate();
  }

  textSanitizerAndValidator(currentTitleText: string, maxTitleLength: number): void {
    if (currentTitleText.length > maxTitleLength) {
      currentTitleText = currentTitleText.substring(0, maxTitleLength);
    }
    currentTitleText = this.ngTypeUtility.string.sanitizeString(currentTitleText);
    currentTitleText = currentTitleText.trim();

    const isValidTitleText = currentTitleText.length >= 1 && currentTitleText.length <= maxTitleLength;

    this.setTitleAndValidate(currentTitleText, isValidTitleText);
  }

  saveFinalizedDayAndTime(): void {
    if (!_4.isEmpty(this.tempSelectedDay)) {
      this.personalReminderData.selectedDay = this.tempSelectedDay;
    }
    if (!_4.isEmpty(this.tempSelectedTime)) {
      this.personalReminderData.selectedTime = this.tempSelectedTime;
    }
    this.updateReminderDueDate();
  }

  saveTempSelectedDayAndTime(selectedDay: IWeekDay, selectedTime: IDay): void {
    if (!_4.isEmpty(selectedDay)) {
      this.tempSelectedDay = selectedDay;
    }
    if (!_4.isEmpty(selectedTime)) {
      this.tempSelectedTime = selectedTime;
    }
  }

  updateReminderDueDate() {
    if (_4.isEmpty(this.personalReminderData.selectedDay)) {
      return;
    }

    let selectedDateTimeString = `${this.personalReminderData.selectedDay.month}
        ${this.personalReminderData.selectedDay.displayDate},
        ${this.personalReminderData.selectedDay.year}`;

    if (!_4.isEmpty(this.personalReminderData.selectedTime) && !this.personalReminderData.isAllDayReminder) {
      selectedDateTimeString = `${selectedDateTimeString} ${this.personalReminderData.selectedTime.time}`;
    }

    this.personalReminderData.reminderDueDate = moment(selectedDateTimeString, 'MMM DD, YYYY HH:mmA').toISOString();
  }

  isDateTimeCorrectlySet(): boolean {
    if (this.personalReminderData.isAllDayReminder) {
      return !_4.isEmpty(this.personalReminderData.selectedDay);
    }
    return !_4.isEmpty(this.personalReminderData.selectedDay) && !_4.isEmpty(this.personalReminderData.selectedTime);
  }

  setReminderNotes(notesText: string): void {
    this.personalReminderData.reminderNotes = notesText;
  }

  read(): IPersonalReminder {
    return this.personalReminderData;
  }

  private seperateTimeAndDate(momentObj: moment.Moment): ISeperatedTimeObject {
    return {
      due_dt: momentObj.format('YYYY-MM-DD'),
      due_tm: momentObj.format('HH:mm:ss.SSSS')
    };
  }

  getReminderAPIValues(): IReminderData {
    const seperatedTime: ISeperatedTimeObject = this.seperateTimeAndDate(
      moment(this.personalReminderData.reminderDueDate)
    );
    return {
      title: this.personalReminderData.reminderTitle,
      description: this.personalReminderData.reminderNotes || '',
      due_dt: seperatedTime.due_dt,
      due_tm: seperatedTime.due_tm,
      status: 'ACTIVE'
    };
  }

  setExistingReminderData(): void {
    const reminderData = this.ngReminderQuery.result;
    this.personalReminderData.reminderID = reminderData.id || '';
    this.personalReminderData.reminderTitle = reminderData.title;
    this.personalReminderData.reminderNotes = reminderData.description;
    this.personalReminderData.reminderDueDate = reminderData.due_ts as string;
    this.isTitleValid = true;

    const dateTime = moment(reminderData.due_ts as string);
    this.personalReminderData.isAllDayReminder = dateTime.hours() === 0 && dateTime.minutes() === 0;

    this.initializeSelectedDateAndTime(dateTime);
  }

  initializeSelectedDateAndTime(dateTime: moment.Moment): void {
    this.personalReminderData.selectedDay = {
      date: dateTime,
      displayDate: dateTime.format('DD'),
      day: dateTime.format('ddd'),
      month: dateTime.format('MMM'),
      year: dateTime.format('YYYY'),
      show: true,
      day_week: dateTime.weekday()
    };

    this.personalReminderData.selectedTime = {
      date: dateTime,
      displayDate: dateTime.format('DD'),
      day: dateTime.format('ddd'),
      day_week: dateTime.weekday(),
      time: dateTime.minutes(Math.ceil(dateTime.minute() / 15) * 15).format('hh:mm A'),
      am: dateTime.format('A') === 'AM',
      selected: true
    };
  }

  getReminderDefaultDate(): string {
    const defaultDate: moment.Moment = moment();
    this.initializeSelectedDateAndTime(defaultDate);
    this.updateReminderDueDate();
    this.tempSelectedDay = this.personalReminderData.selectedDay;
    this.tempSelectedTime = this.personalReminderData.selectedTime;
    return `${defaultDate.format('MMM DD, YYYY')} at ${defaultDate.format('hh:mm A')}`;
  }

  getDateTimeString(fromModal: boolean): string {
    if (!this.isDateTimeCorrectlySet()) {
      return this.getReminderDefaultDate();
    }

    const day =
      fromModal && !_4.isEmpty(this.tempSelectedDay) ? this.tempSelectedDay : this.personalReminderData.selectedDay;
    const time =
      fromModal && !_4.isEmpty(this.tempSelectedTime) ? this.tempSelectedTime : this.personalReminderData.selectedTime;

    const date = `${day.month}
                    ${day.displayDate},
                    ${day.year}`;

    if (this.personalReminderData.isAllDayReminder) {
      return date;
    } else {
      return `${date} at ${time.time}`;
    }
  }

  isModalDateTimeValid() {
    if (this.personalReminderData.isAllDayReminder) {
      return !_4.isEmpty(this.tempSelectedDay);
    }
    return !_4.isEmpty(this.tempSelectedDay) && !_4.isEmpty(this.tempSelectedTime);
  }

  clear(): void {
    this.personalReminderData = {} as IPersonalReminder;
    this.isTitleValid = false;
  }
}

angular.module('data-ng-personal-reminders').service('personalReminderService', PersonalReminderService);



// WEBPACK FOOTER //
// ./app/vendor/data-ng-personal-reminders/src/services/personal_reminder_service.ts